import React, { useState, useEffect, useRef } from 'react';

interface CommonProps {
  isOpen?: boolean;
  label: string;
  items: string[];
  onSelect: (val: string) => void;
  value?: string | null;
  searchable?: boolean;
}
interface DropDownProps {
  isOpen?: boolean;
}

export const DropDown: React.FC<DropDownProps> = ({ isOpen }) => (
  <svg
    className={`w-4 h-4 text-gray-600 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    viewBox="0 0 24 24"
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
  </svg>
);

// DropdownCard component
const DropdownCard: React.FC<CommonProps> = ({
  label,
  items = [],
  onSelect,
  value,
  searchable
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState<string>('');
  const [filteredItems, setFilteredItems] = useState<string[]>(items || []);
  const dropdownRef = useRef<any>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleItemClick = (item: string) => {
    setSelected(item);
    setInputValue(item);
    setIsOpen(false);
    onSelect?.(item);
  };

  // Close dropdown if clicked outside
  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Sync selected/inputValue when parent value changes
  useEffect(() => {
    if (value !== undefined && value !== null) {
      setSelected(value);
      setInputValue(value);
    } else {
      setSelected(null);
      setInputValue('');
    }
  }, [value]);

  // When items change (content-wise), reset filteredItems.
  // Note: parent may recreate the items array each render; compare content to avoid flicker.
  const prevItemsJoinRef = useRef<string | null>(null);
  useEffect(() => {
    const join = (items || []).join('\u241F');
    if (prevItemsJoinRef.current !== join) {
      prevItemsJoinRef.current = join;
      setFilteredItems(items || []);
    }
  }, [items]);

  // Focus input when dropdown opens (only if searchable)
  useEffect(() => {
    if (isOpen && searchable) {
      // show current selected value in input so user can edit
      setInputValue((prev) => prev ?? selected ?? '');
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [isOpen, selected, searchable]);

  useEffect(() => {
    if (!searchable) return;
    const q = inputValue || '';
    if (!q) {
      setFilteredItems(items || []);
    } else {
      const lower = q.toLowerCase();
      setFilteredItems((items || []).filter((it) => it.toLowerCase().includes(lower)));
    }
  }, [inputValue, items, searchable]);

  return (
    <div ref={dropdownRef} className="relative w-full">
      <div className="w-full">
        <div
          className={`relative w-full flex items-center justify-between rounded-xl bg-white shadow-sm  cursor-text`}
          onClick={() => setIsOpen(true)}
        >
          <input
            ref={inputRef}
            className="w-full outline-none px-3 py-2 text-[15px] bg-transparent  pr-[35px]"
            value={searchable ? inputValue : selected || ''}
            onChange={(e) => searchable && setInputValue(e.target.value)}
            onFocus={() => setIsOpen(true)}
            placeholder={label}
            readOnly={!searchable}
          />
          <div className="absolute right-[10px]">
            <DropDown isOpen={isOpen} />
          </div>
        </div>
      </div>

      {isOpen && (
        <ul className="absolute z-10 w-full mt-1 bg-white border border-[#E7E7E7] rounded-xl shadow-lg max-h-[238px] overflow-scroll">
          {filteredItems && filteredItems.length > 0 ? (
            filteredItems.map((item, index) => (
              <li
                key={index}
                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer mb-0"
                onClick={() => handleItemClick(item)}
              >
                {item}
              </li>
            ))
          ) : (
            <li className="px-4 py-2 text-sm text-gray-500">No results</li>
          )}
        </ul>
      )}
    </div>
  );
};

export default DropdownCard;
